/* eslint-disable max-classes-per-file */
/* eslint-disable @typescript-eslint/naming-convention */
import { AnalyticsEvent } from '@shure/shared/models';

import { CdmAnalyticsEvents } from './cdm-analytics-events.enum';
import { AnalyticsCommonDeviceInfo, AnalyticsLocation } from './common.model';

export enum DeviceAction {
	NameChange = 'name_change',
	Reboot = 'reboot',
	Identify = 'identify',
	AddTag = 'add_tag',
	RemoveTag = 'remove_tag',
	RemoveDevice = 'remove_device',
	RoomProxyingChange = 'room_proxying_change'
}

export type CancelledDeviceAction = DeviceAction.Reboot | DeviceAction.RemoveDevice;

export type DeviceActionLocation = AnalyticsLocation.DevicesTable | AnalyticsLocation.PropertyPanel;

export type DeviceIdentifyLocation =
	| AnalyticsLocation.PropertyPanel
	| AnalyticsLocation.DevicesTable
	| AnalyticsLocation.JobDetailsTable
	| AnalyticsLocation.FirmwareUpdateDialogTable
	| AnalyticsLocation.CreateJobTable;

export type DeviceSelectionLocation = AnalyticsLocation.DevicesTable;

/**
 * To measure which per-device settings change operations users perform.
 */
export class DeviceActionEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.DeviceAction;
	public readonly properties: {
		devices: AnalyticsCommonDeviceInfo[];
		event_sub_type: DeviceAction;
		location: DeviceActionLocation | DeviceIdentifyLocation;
	};

	constructor(
		devices: AnalyticsCommonDeviceInfo[],
		eventSubType: DeviceAction,
		location: DeviceActionLocation | DeviceIdentifyLocation
	) {
		this.properties = {
			devices: devices,
			event_sub_type: eventSubType,
			location: location
		};
	}
}

/**
 * To measure when a user initiates a settings change for a device, but when presented with a confirmation dialog
 * cancels instead of proceeding.
 *
 * Note that only disruptive, or "no going back" operations present the user with a dialog for the user to confirm before being committed.
 */
export class DeviceActionCancelledEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.DeviceActionCancelled;
	public readonly properties: {
		devices: AnalyticsCommonDeviceInfo[];
		event_sub_type: CancelledDeviceAction;
		location: DeviceActionLocation;
	};

	constructor(
		devices: AnalyticsCommonDeviceInfo[],
		eventSubType: CancelledDeviceAction,
		location: DeviceActionLocation
	) {
		this.properties = {
			devices: devices,
			event_sub_type: eventSubType,
			location: location
		};
	}
}

export class DeviceSelectionEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.DeviceSelection;
	public readonly properties: {
		location: DeviceSelectionLocation;
		devices: AnalyticsCommonDeviceInfo[];
	};

	constructor(location: DeviceSelectionLocation, devices: AnalyticsCommonDeviceInfo[]) {
		this.properties = {
			location: location,
			devices: devices
		};
	}
}

export class PropertyPanelOpenedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.PropertyPanelOpened;
	public readonly properties: {
		devices: AnalyticsCommonDeviceInfo[];
	};

	constructor(devices: AnalyticsCommonDeviceInfo[]) {
		this.properties = {
			devices: devices
		};
	}
}

export class PropertyPanelClosedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.PropertyPanelClosed;
	public readonly properties = {};
}
